import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TranscriptSegment {
  id: string;
  content_id: string;
  start_seconds: number;
  end_seconds: number;
  text: string;
  confidence: number | null;
  language: string;
  created_at: string;
}

export function useMediaTranscript(contentId: string | undefined) {
  return useQuery({
    queryKey: ['media-transcript', contentId],
    queryFn: async () => {
      if (!contentId) return [];
      
      const { data, error } = await supabase
        .from('media_transcripts')
        .select('*')
        .eq('content_id', contentId)
        .order('start_seconds', { ascending: true });
      
      if (error) throw error;
      return data as TranscriptSegment[];
    },
    enabled: !!contentId,
  });
}

export function useTranscriptStatus(contentId: string | undefined) {
  const queryClient = useQueryClient();
  const [realtimeStatus, setRealtimeStatus] = useState<string | null>(null);

  // Initial fetch
  const query = useQuery({
    queryKey: ['transcript-status', contentId],
    queryFn: async () => {
      if (!contentId) return null;
      
      const { data, error } = await supabase
        .from('content')
        .select('transcript_status')
        .eq('id', contentId)
        .single();
      
      if (error) throw error;
      return data?.transcript_status as string | null;
    },
    enabled: !!contentId,
  });

  // Realtime subscription for status updates
  useEffect(() => {
    if (!contentId) return;

    const channel = supabase
      .channel(`transcript-status-${contentId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'content',
          filter: `id=eq.${contentId}`,
        },
        (payload) => {
          const newStatus = (payload.new as any).transcript_status;
          console.log('[useTranscriptStatus] Realtime update:', newStatus);
          setRealtimeStatus(newStatus);
          
          // If status changed to 'ready', refetch transcript data
          if (newStatus === 'ready') {
            queryClient.invalidateQueries({ queryKey: ['media-transcript', contentId] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contentId, queryClient]);

  // Return realtime status if available, otherwise query data
  return {
    ...query,
    data: realtimeStatus ?? query.data,
  };
}

export function useGenerateTranscript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contentId: string) => {
      const { data, error } = await supabase.functions.invoke('extract-media-transcript', {
        body: { contentId },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, contentId) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['media-transcript', contentId] });
      queryClient.invalidateQueries({ queryKey: ['transcript-status', contentId] });
    },
  });
}
